import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import WorkflowApi from "../../api/WorkflowApi";
import { isMobile } from "react-device-detect";
import { getEntitlementsSection } from "../../utils/entitlements";
import {
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Modal,
} from "react-bootstrap";
import {
  DatatableWrapper,
  TableBody,
  TableHeader,
  PaginationOptions,
  Filter,
  Pagination,
} from "react-bs-datatable";
import { FaPlus, FaBolt, FaSearch, FaEdit, FaTrash } from "react-icons/fa";

const WorkflowList = () => {
  const navigate = useNavigate();
  const [body, setBody] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const workflowEnt = getEntitlementsSection("workflow", {});

  useEffect(() => {
    if (workflowEnt.allowViewWorkflow === false) {
      navigate("/setup", { replace: true });
      return;
    }
    fetchWorkflows();
  }, [navigate, workflowEnt.allowViewWorkflow]);

  async function fetchWorkflows() {
    try {
      const result = await WorkflowApi.fetchWorkflows();
      if (result?.data) {
        setBody(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch workflows", error);
    }
  }

  const handleEdit = (workflow) => {
    navigate(`/setup/workflows/${workflow.id}/edit`);
  };

  const handleDeleteClick = (workflow) => {
    setSelectedWorkflow(workflow);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedWorkflow) return;

    setDeleting(true);
    try {
      await WorkflowApi.deleteWorkflow(selectedWorkflow.id);
      setShowDeleteModal(false);
      setSelectedWorkflow(null);
      // Refresh the workflows list
      fetchWorkflows();
    } catch (error) {
      console.error("Failed to delete workflow:", error);
      alert("Failed to delete the workflow. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedWorkflow(null);
  };

  const header = [];
  if (!isMobile) {
    header.push(
      {
        title: "Workflow Name",
        prop: "workflow_name",
        isFilterable: true,
        cell: (row) => (
          <Link to={`/setup/workflows/${row.id}`} state={row} className="text-decoration-none fw-semibold">
            <FaBolt className="me-2 text-warning" size={14} />
            {row.workflow_name}
          </Link>
        ),
      },
      { title: "Object Name", prop: "object_name", isFilterable: true },
      { title: "Event Type", prop: "event_type", isFilterable: true },
      {
        title: "Status",
        prop: "is_active",
        isFilterable: true,
        cell: (row) => (
          <Badge bg={row.is_active ? "success" : "secondary"}>
            {row.is_active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      ...(workflowEnt.allowCreateWorkflow !== false
        ? [
            {
              title: "Actions",
              prop: "actions",
              cell: (row) => (
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleEdit(row)}
                    title="Edit"
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteClick(row)}
                    title="Delete"
                  >
                    <FaTrash />
                  </Button>
                </div>
              ),
            },
          ]
        : []),
    );
  } else {
    header.push({
      title: "Info",
      prop: "workflow_name",
      isFilterable: true,
      cell: (row) => (
        <div className="mobilecard">
          <Link to={`/setup/workflows/${row.id}`} state={row}>
            <FaBolt className="me-2 text-warning" size={14} />
            {row.workflow_name}
          </Link>
          <span>Object: {row.object_name}</span>
          <span>Event: {row.event_type}</span>
          <Badge bg={row.is_active ? "success" : "secondary"} className="mt-2">
            {row.is_active ? "Active" : "Inactive"}
          </Badge>
          {workflowEnt.allowCreateWorkflow !== false && (
            <div className="d-flex gap-2 mt-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleEdit(row)}
                title="Edit"
              >
                <FaEdit className="me-1" />
                Edit
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDeleteClick(row)}
                title="Delete"
              >
                <FaTrash className="me-1" />
                Delete
              </Button>
            </div>
          )}
        </div>
      ),
    });
  }

  const labels = {
    beforeSelect: " ",
  };

  return (
    <div className="workflow-manager">
      <style jsx>{`
        .workflow-manager .header-flex {
          display: flex !important;
          align-items: center !important;
          margin-bottom: 1rem !important;
        }
        .workflow-manager .header-icon {
          margin-right: 0.5rem !important;
        }
        .workflow-manager .card-header-flex {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
        }
        .workflow-manager .filter-flex {
          display: flex !important;
          align-items: center !important;
          margin-bottom: 1rem !important;
        }
      `}</style>

      {/* Header Section */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="p-4">
          <div className="header-flex">
            <div className="header-icon">
              <div className="bg-warning bg-opacity-10 p-3 rounded-circle d-inline-flex">
                <FaBolt size={32} className="text-warning" />
              </div>
            </div>
            <div>
              <h2 className="mb-1 fw-bold">Workflow Manager</h2>
              <p className="text-muted mb-0">
                Automate actions like sending emails, creating tasks, updating fields, and more based on record changes
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Workflows List */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <div className="card-header-flex">
            <h5 className="mb-0 fw-semibold">
              <FaBolt className="me-2 text-warning" />
              <span className="me-2 text-dark"> All Workflows</span>
              <Badge bg="secondary" className="ms-2">{body.length}</Badge>
            </h5>
            {getEntitlementsSection("workflow", {}).allowCreateWorkflow !== false && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate("/setup/workflows/create")}
              >
                <FaPlus className="me-2" size={12} />
                New Workflow
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-3">
          <DatatableWrapper
            body={body}
            headers={header}
            totalRows={body.length}
            paginationOptionsProps={{
              initialState: {
                rowsPerPage: 10,
                options: [5, 10, 15, 20],
              },
            }}
          >
            <Row className="filter-flex">
              <Col lg={4} md={6} className="mb-2 mb-lg-0">
                <Filter placeholder="Search workflows..." />
              </Col>
              <Col lg={2} md={6} className="mb-2 mb-lg-0">
                <PaginationOptions labels={labels} />
              </Col>
              <Col lg={6} className="text-end">
                <small className="text-muted">
                  Showing {body.length} workflow{body.length !== 1 ? 's' : ''}
                </small>
              </Col>
            </Row>

            <div className="table-responsive">
              <Table hover className="mb-0">
                <TableHeader />
                <TableBody />
              </Table>
            </div>

            <div className="mt-3 d-flex justify-content-center">
              <Pagination />
            </div>
          </DatatableWrapper>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the workflow <strong>"{selectedWorkflow?.workflow_name}"</strong>?
          <br />
          <small className="text-muted">This action cannot be undone.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WorkflowList;
